import React, { useEffect } from 'react';
import { Outlet, Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import Sidebar from '@/components/Sidebar';
import Breadcrumb from '@/components/Breadcrumb';
import NotificationBell from '@/components/NotificationBell';
import ShareFloatButton from '@/components/ShareFloatButton';
import GlobalSearch from '@/components/GlobalSearch';
import WechatUpgradeBanner from '@/components/WechatUpgradeBanner';
import { useAuthStore } from '@/stores/authStore';
import { useUIStore } from '@/stores/uiStore';
import { getMe } from '@/api/auth';

const DRAWER_WIDTH = 240;
const DRAWER_WIDTH_COLLAPSED = 64;

const MainLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { sidebarCollapsed, toggleSidebar, mobileSidebarOpen, openMobileSidebar, closeMobileSidebar } = useUIStore();
  const { user, currentTenant, clearAuth, setIsPlatformAdmin, setIsChannel, setConsultantStatus, setCertStatus, setIdentity, setUser } = useAuthStore();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  // 进入主框架即刷新全局平台管理员标志与身份：切租户后或旧会话（未重新登录）也能正确显示菜单与权限
  useEffect(() => {
    getMe()
      .then((r) => {
        const v = r.data?.isPlatformAdmin;
        if (typeof v === 'boolean') setIsPlatformAdmin(v);
        const ch = r.data?.isChannel;
        if (typeof ch === 'boolean') setIsChannel(ch);
        const cs = r.data?.consultantStatus;
        if (cs === 'active' || cs === 'pending' || cs === 'disabled' || cs === null) {
          setConsultantStatus(cs);
        }
        const cert = r.data?.certStatus;
        if (cert === 'approved' || cert === 'pending' || cert === 'rejected' || cert === null) {
          setCertStatus(cert);
        }
        const id = r.data?.user?.identity;
        if (id === 'owner' || id === 'executive' || id === 'consultant' || id === 'other' || id === null) {
          setIdentity(id);
        }
        // 同步微信占位标志：服务端为权威来源，避免旧会话持久化状态显示错位
        const meUser = r.data?.user;
        if (meUser && typeof meUser.isWechatPlaceholder === 'boolean') {
          const cur = useAuthStore.getState().user;
          if (cur && cur.isWechatPlaceholder !== meUser.isWechatPlaceholder) {
            setUser({ ...cur, isWechatPlaceholder: meUser.isWechatPlaceholder });
          }
        }
      })
      .catch(() => {});
  }, [setIsPlatformAdmin, setIsChannel, setConsultantStatus, setCertStatus, setIdentity]);

  const drawerWidth = sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    clearAuth();
    handleMenuClose();
    navigate('/login');
  };

  const handleSwitchTenant = () => {
    handleMenuClose();
    navigate('/tenant-select');
  };

  const handleDrawerToggle = () => {
    if (isMobile) {
      openMobileSidebar();
    } else {
      toggleSidebar();
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* 侧边栏：移动端用临时 Drawer，桌面端用永久 Drawer */}
      {isMobile ? (
        <Sidebar
          width={DRAWER_WIDTH}
          collapsed={false}
          mobileOpen={mobileSidebarOpen}
          onMobileClose={closeMobileSidebar}
        />
      ) : (
        <Sidebar width={drawerWidth} collapsed={sidebarCollapsed} />
      )}

      {/* 主内容区
          关键：本 Box 是根 row-flex 的 item，min-width:auto 会被宽内容（如 FMEA
          1740px 表格）撑开。撑开链条：本层 → main → TableContainer → Table，
          只有从这一层截断，宽表格才会落到 TableContainer 的 overflow 内滚动。
          注意 min-width:auto 只在「主轴」生效 —— main 是 column-flex 的 item
          （主轴垂直），在它身上加 minWidth:0 无效，必须加在这一层。 */}
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          marginLeft: isMobile ? 0 : `${drawerWidth}px`,
          transition: isMobile ? 'none' : 'margin-left 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* 顶栏 */}
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            backgroundColor: '#fff',
            borderBottom: '1px solid #e0e0e0',
            color: '#333',
            width: isMobile ? '100%' : `calc(100% - ${drawerWidth}px)`,
            marginLeft: isMobile ? 0 : `${drawerWidth}px`,
            transition: isMobile ? 'none' : 'width 0.2s ease, margin-left 0.2s ease',
          }}
        >
          <Toolbar variant="dense" sx={{ px: { xs: 1, sm: 2 } }}>
            <IconButton edge="start" onClick={handleDrawerToggle} sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
            <Breadcrumb />
            <GlobalSearch />
            <NotificationBell />
            {currentTenant && !isMobile && (
              <Typography variant="body2" sx={{ mr: 2, color: '#666' }}>
                {currentTenant.name}
              </Typography>
            )}
            <IconButton onClick={handleMenuOpen} size="small">
              <Avatar
                src={user?.avatar || undefined}
                sx={{ width: 32, height: 32, bgcolor: '#1A365D', fontSize: 14 }}
              >
                {user?.displayName?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{ sx: { mt: 1, minWidth: 180 } }}
            >
              <MenuItem disabled>
                <ListItemText primary={user?.displayName} secondary={user?.phone} />
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleSwitchTenant}>
                <ListItemIcon><SwitchAccountIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="切换租户" />
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="退出登录" />
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* 页面内容 */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            // minWidth:0 对本层（column-flex item）非必需，但作为多层嵌套的兜底保留。
            // 不使用 overflowX:'hidden'：一旦某个祖先 overflow 非 visible，
            // 表格的 sticky 表头就会改相对该祖先定位而失效，滚动时表头吸不住。
            minWidth: 0,
            px: { xs: 1.5, sm: 2, md: 3 },
            py: { xs: 2, md: 3 },
            mt: '48px',
            backgroundColor: '#f5f5f5',
          }}
        >
          <React.Suspense fallback={<Box sx={{ p: 3, textAlign: 'center' }}>加载中...</Box>}>
            <WechatUpgradeBanner />
            <Outlet />
          </React.Suspense>
        </Box>
      </Box>
      <ShareFloatButton />
    </Box>
  );
};

export default MainLayout;
